import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

interface SEOProps {
  title?: string
  description?: string
  image?: string
  url?: string
}

/**
 * SEO component that updates meta tags dynamically based on current route
 */
export const SEO = ({
  title,
  description,
  image = 'https://zwizle.com/og-image.jpg',
  url,
}: SEOProps) => {
  const location = useLocation()
  const currentUrl = url || `https://zwizle.com${location.pathname}`

  // Default title and descriptions
  const defaultTitle = 'ZWIZLE - Shona Word Game & Puzzles'
  const defaultDescription =
    'Play ZWIZLE - the best Shona word games and puzzles. Challenge yourself and learn Shona language while having fun!'

  // Get page-specific title based on route
  const getPageTitle = () => {
    if (title) return title

    if (location.pathname === '/') return defaultTitle
    if (location.pathname.startsWith('/play/')) {
      const mode = location.pathname.split('/').pop()
      return `${mode}-Letter Challenge | ZWIZLE`
    }
    if (location.pathname === '/complete') return 'Game Completed | ZWIZLE'

    return defaultTitle
  }

  // Get page-specific description based on route
  const getPageDescription = () => {
    if (description) return description

    if (location.pathname.startsWith('/play/')) {
      const mode = location.pathname.split('/').pop()
      return `Challenge yourself with our ${mode}-letter Shona word puzzle. Perfect your language skills while having fun!`
    }

    return defaultDescription
  }

  const pageTitle = getPageTitle()
  const pageDescription = getPageDescription()

  useEffect(() => {
    // Update document title
    document.title = pageTitle

    // Update meta tags
    const metaDescription = document.querySelector('meta[name="description"]')
    if (metaDescription) {
      metaDescription.setAttribute('content', pageDescription)
    }

    // Update Open Graph meta tags
    const ogTitle = document.querySelector('meta[property="og:title"]')
    const ogDescription = document.querySelector(
      'meta[property="og:description"]'
    )
    const ogUrl = document.querySelector('meta[property="og:url"]')
    const ogImage = document.querySelector('meta[property="og:image"]')

    if (ogTitle) ogTitle.setAttribute('content', pageTitle)
    if (ogDescription) ogDescription.setAttribute('content', pageDescription)
    if (ogUrl) ogUrl.setAttribute('content', currentUrl)
    if (ogImage) ogImage.setAttribute('content', image)

    // Update Twitter meta tags
    const twitterTitle = document.querySelector('meta[name="twitter:title"]')
    const twitterDescription = document.querySelector(
      'meta[name="twitter:description"]'
    )
    const twitterUrl = document.querySelector('meta[name="twitter:url"]')
    const twitterImage = document.querySelector('meta[name="twitter:image"]')

    if (twitterTitle) twitterTitle.setAttribute('content', pageTitle)
    if (twitterDescription)
      twitterDescription.setAttribute('content', pageDescription)
    if (twitterUrl) twitterUrl.setAttribute('content', currentUrl)
    if (twitterImage) twitterImage.setAttribute('content', image)

    // Update canonical URL
    const canonicalLink = document.querySelector('link[rel="canonical"]')
    if (canonicalLink) canonicalLink.setAttribute('href', currentUrl)
  }, [pageTitle, pageDescription, currentUrl, location.pathname, image])

  // This component doesn't render anything visible
  return null
}
